import React, { useState, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { QRCodeSVG } from 'qrcode.react'
import { ShareIcon, DocumentArrowDownIcon } from '@heroicons/react/24/outline'
import TemplateRenderer from '../../templates/components/TemplateRenderer'
import LoadingSpinner from '../../../components/common/LoadingSpinner'
import { useVCardStore } from '../../../store/vCardStore'
import toast from 'react-hot-toast'

function PublicVCardPage() {
  const { slug } = useParams()
  const { currentVCard, loading, error, fetchVCardBySlug } = useVCardStore()
  const [isChecking, setIsChecking] = useState(true)
  const [showQR, setShowQR] = useState(false)
  const [shareSupported, setShareSupported] = useState(false)

  useEffect(() => {
    const loadVCard = async () => {
      if (!slug) return
      
      setIsChecking(true)
      
      // First try with the slug as-is
      await fetchVCardBySlug(slug)
      
      setIsChecking(false)
    }
    
    loadVCard()
  }, [slug, fetchVCardBySlug])

  useEffect(() => {
    // Check if Web Share API is supported
    setShareSupported(typeof navigator.share !== 'undefined')
  }, [])

  useEffect(() => {
    if (error) {
      toast.error(error)
    }
  }, [error])

  const handleShare = async () => {
    const shareUrl = window.location.href
    const shareTitle = `${currentVCard?.name} - ${currentVCard?.position}`
    const shareText = `Conoce la Dkard de ${currentVCard?.name}`

    if (shareSupported) {
      try {
        await navigator.share({
          title: shareTitle,
          text: shareText,
          url: shareUrl,
        })
        toast.success('¡Dkard compartida!')
      } catch (err) {
        // User cancelled share
        console.log('Share cancelled')
      }
    } else {
      // Fallback: copy to clipboard
      try {
        await navigator.clipboard.writeText(shareUrl)
        toast.success('Enlace copiado al portapapeles')
      } catch (err) {
        toast.error('Error al copiar el enlace')
      }
    }
  }

  const handleDownloadVCF = () => {
    if (!currentVCard) return

    const vcfContent = generateVCF(currentVCard)
    const blob = new Blob([vcfContent], { type: 'text/vcard' })
    const url = URL.createObjectURL(blob)
    const link = document.createElement('a')
    link.href = url
    link.download = `${currentVCard.name.replace(/\s+/g, '_')}.vcf`
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)
    
    toast.success('Contacto descargado')
  }

  const generateVCF = (vCard) => {
    const lines = [
      'BEGIN:VCARD',
      'VERSION:3.0',
      `FN:${vCard.name}`,
      `TITLE:${vCard.position}`,
      vCard.email ? `EMAIL:${vCard.email}` : '',
      vCard.phone ? `TEL:${vCard.phone}` : '',
      vCard.bio ? `NOTE:${vCard.bio}` : '',
      'END:VCARD'
    ].filter(Boolean)

    return lines.join('\n')
  }

  if (loading || isChecking) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <LoadingSpinner size="large" />
      </div>
    )
  }

  if (!currentVCard || !currentVCard.public || currentVCard.active === false) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-100">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-2">Dkard no disponible</h2>
          <p className="text-gray-600">Esta tarjeta digital es privada o ha sido deshabilitada.</p>
        </div>
      </div>
    )
  }

  const currentUrl = window.location.href

  return (
    <div className="public-vcard-page">
      {/* Floating Action Buttons */}
      <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-3">
        <button
          onClick={handleShare}
          className="w-14 h-14 rounded-full bg-blue-500 text-white shadow-lg hover:bg-blue-600 transition-all hover:scale-110 flex items-center justify-center"
          title="Compartir"
        >
          <ShareIcon className="w-6 h-6" />
        </button>
        
        <button
          onClick={() => setShowQR(!showQR)}
          className="w-14 h-14 rounded-full bg-green-500 text-white shadow-lg hover:bg-green-600 transition-all hover:scale-110 flex items-center justify-center"
          title="Mostrar QR"
        >
          <svg className="w-6 h-6" fill="currentColor" viewBox="0 0 24 24">
            <path d="M3 11h8V3H3v8zm2-6h4v4H5V5zM3 21h8v-8H3v8zm2-6h4v4H5v-4zM13 3v8h8V3h-8zm6 6h-4V5h4v4zM13 13h2v2h-2zM15 13h2v2h-2zM13 15h2v2h-2zM17 13h2v2h-2zM19 13h2v2h-2zM17 15h2v2h-2zM15 15h2v2h-2zM19 15h2v2h-2zM17 17h2v2h-2zM13 17h2v2h-2zM15 17h2v2h-2zM19 17h2v2h-2zM21 21h-8v-2h2v-2h-2v-2h2v-2h-2v-2h2v-2h-2v-2h8v8z"/>
          </svg>
        </button>
        
        <button
          onClick={handleDownloadVCF}
          className="w-14 h-14 rounded-full bg-purple-500 text-white shadow-lg hover:bg-purple-600 transition-all hover:scale-110 flex items-center justify-center"
          title="Descargar Contacto"
        >
          <DocumentArrowDownIcon className="w-6 h-6" />
        </button>
      </div>

      {/* QR Code Modal */}
      {showQR && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-sm w-full">
            <div className="text-center mb-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Código QR</h3>
              <p className="text-sm text-gray-600">Escanea para guardar esta Dkard</p>
            </div>
            <div className="flex justify-center mb-6">
              <QRCodeSVG
                value={currentUrl}
                size={200}
                level="H"
                includeMargin={true}
              />
            </div>
            <button
              onClick={() => setShowQR(false)}
              className="w-full py-3 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
            >
              Cerrar
            </button>
          </div>
        </div>
      )}

      {/* Main Content */}
      <div className="min-h-screen bg-gray-100">
        <TemplateRenderer
          templateId={currentVCard.templateId}
          vCard={currentVCard}
        />
      </div>
    </div>
  )
}

export default PublicVCardPage
